import type { User } from "@/models/user";
import { normalizeAvatarFields } from "@/slices/auth/authAvatar";

const OPS_TENANT_KIND = "ops";

type SessionPayload = {
  "st-ev"?: unknown;
  email?: unknown;
  fullName?: unknown;
  firstName?: unknown;
  groups?: unknown;
  is_oauth_user?: unknown;
  lastName?: unknown;
  phoneNumber?: unknown;
  role?: unknown;
  spAId?: unknown;
  spEmail?: unknown;
  spPendingWorkspaceSelection?: unknown;
  spPTGuid?: unknown;
  spTid?: unknown;
  spTGuid?: unknown;
  spTKind?: unknown;
  spUGuid?: unknown;
  sub?: unknown;
};

function normalizeText(value: unknown): string {
  return String(value || "").trim();
}

function normalizeRole(value: unknown): number {
  const role = Number(value ?? 0);
  return Number.isFinite(role) ? role : 0;
}

function normalizeGroups(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function normalizeTenantKind(value: unknown): string {
  return normalizeText(value).toLowerCase();
}

function getUserIdentity(user?: Record<string, unknown> | null): string {
  return normalizeText(user?.spUserGuid || user?.guid || user?.id);
}

function getUserTenantGuid(user?: Record<string, unknown> | null): string {
  return normalizeText(user?.tenantGuid || user?.tenant_guid || user?.spTGuid);
}

function getUserAccountGuid(user?: Record<string, unknown> | null): string {
  return normalizeText(user?.accountGuid || user?.account_guid || user?.spAId);
}

function isOpsTenantUser(user?: Record<string, unknown> | null): boolean {
  return normalizeTenantKind(user?.tenantKind || user?.tenant_kind || user?.spTKind) === OPS_TENANT_KIND;
}

export function isEmailVerifiedClaim(payload: unknown): boolean {
  const claim = (payload as { "st-ev"?: unknown } | null | undefined)?.["st-ev"];
  if (typeof claim === "boolean") {
    return claim;
  }

  return Boolean((claim as { v?: unknown } | null | undefined)?.v);
}

export function isEmailVerificationPending(payload: unknown): boolean {
  const claim = (payload as { "st-ev"?: unknown } | null | undefined)?.["st-ev"];
  if (typeof claim === "boolean") {
    return !claim;
  }

  if (claim && typeof claim === "object" && "v" in claim) {
    return !(claim as { v?: unknown }).v;
  }

  return false;
}

export function hasPendingWorkspaceSelection(user?: Record<string, unknown> | null): boolean {
  if (isOpsTenantUser(user)) {
    return false;
  }

  return Boolean(user?.pendingWorkspaceSelection || user?.spPendingWorkspaceSelection);
}

export function buildUserFromSessionPayload(
  payload: SessionPayload | null | undefined,
  fallbackEmail = ""
): User | null {
  const spUserGuid = normalizeText(payload?.spUGuid);
  const email = normalizeText(payload?.spEmail || payload?.email || fallbackEmail);
  const userId = spUserGuid || normalizeText(payload?.sub) || email;

  if (!userId) {
    return null;
  }

  const rawUser: Partial<User> = {
    id: userId,
    guid: spUserGuid,
    email,
    name: normalizeText(payload?.fullName || payload?.firstName || email),
    location: "",
    role: normalizeRole(payload?.role),
    groups: normalizeGroups(payload?.groups),
    spUserGuid,
    stUserGuid: normalizeText(payload?.sub),
    tenantGuid: normalizeText(payload?.spTGuid),
    tenantKind: normalizeText(payload?.spTKind),
    accountGuid: normalizeText(payload?.spAId),
    spAId: normalizeText(payload?.spAId),
    pendingWorkspaceSelection: Boolean(payload?.spPendingWorkspaceSelection),
    spPendingWorkspaceSelection: Boolean(payload?.spPendingWorkspaceSelection),
    isOauthUser: Boolean(payload?.is_oauth_user),
    spPTGuid: normalizeText(payload?.spPTGuid),
    tenantName: normalizeText(payload?.spTid),
    spTid: normalizeText(payload?.spTid),
    spTGuid: normalizeText(payload?.spTGuid),
    spTKind: normalizeText(payload?.spTKind),
    firstName: normalizeText(payload?.firstName),
    lastName: normalizeText(payload?.lastName),
    phoneNumber: normalizeText(payload?.phoneNumber)
  };

  rawUser.pendingWorkspaceSelection = hasPendingWorkspaceSelection(rawUser);
  rawUser.spPendingWorkspaceSelection = rawUser.pendingWorkspaceSelection;

  return normalizeAvatarFields(rawUser as User);
}

export function shouldHydrateSessionUser(
  currentUser: Record<string, unknown> | null | undefined,
  nextUser: User | null
): boolean {
  if (!nextUser) {
    return false;
  }

  if (!currentUser) {
    return true;
  }

  const currentIdentity = getUserIdentity(currentUser);
  const nextIdentity = getUserIdentity(nextUser);
  if (!currentIdentity || currentIdentity !== nextIdentity) {
    return true;
  }

  if (getUserTenantGuid(currentUser) !== getUserTenantGuid(nextUser)) {
    return true;
  }

  if (getUserAccountGuid(currentUser) !== getUserAccountGuid(nextUser)) {
    return true;
  }

  if (hasPendingWorkspaceSelection(currentUser) !== hasPendingWorkspaceSelection(nextUser)) {
    return true;
  }

  const currentEmail = normalizeText(currentUser.email);
  const nextEmail = normalizeText(nextUser.email);
  return Boolean(nextEmail && currentEmail !== nextEmail);
}
